'use client';
import { betTypeService } from '@/services/bettype.service';
import { getSportSetting } from '@/services/settings.service';
import { TranslateType } from '@/types/bet-type-settings';
import { getSortedESportMarkets, getSortedMarkets } from '@/utils/odds-utils';
import { Market, SportMatchModel } from '@nf/types/odds';
import { Box, IO, MaybeMatch, matchEquals } from '@nf/utils-common/fpw/monadic';
import { itemsEmptyCheck } from '@nf/utils-common/fpw/utils';
import { useTranslations } from 'next-intl';
import { Fragment, useEffect, useRef, useState } from 'react';
import { oddsPageService } from '@/features/odds-page/services/odds-page.service';
import { matchStyles } from '../match.style';
import { OddsButton } from '@/components/widgets/odds-button';
import { MoreLinesButton } from '../../asian-lines';
import { css } from 'styled-system/css';
import { useGetBetTypeSetting, useGetGoal } from '@/hooks';
import { usePreferredMainBetType } from '@/features/odds-page/hooks/use-preferred-main-bet-type';
import { BetChoice } from '@/features/odds-page/types';
import { asianLineBetTypes } from '@/settings/bet-type-settings';
import { OddsTablePagination } from '../odds-table-pagination';
import clsx from 'clsx';
import { useOddsTableMarsContext } from '@/features/odds-table-mars/context';
import { MatchOddsLoadingWrapper } from '@/features/odds-page/match-odds-loading-wrapper';
import { useBreakpoint } from '@/features/breakpoint/contexts';
import { Breakpoint } from '@/features/breakpoint';
import { toChunks } from '@nf/utils-common';

type BetType = number;
type ResourceId = string | undefined;
type BetTypeGroup = [BetType, ResourceId];
type BetTypeMarket = [BetTypeGroup, Market | undefined];

type MatchBettypesProps = {
	match: SportMatchModel,
	markets: Market[],
	isLoading: boolean,
	hasMoreAsianLines: boolean,
	homeTeamName: string;
	awayTeamName: string;
}

export const MatchBettypes = ({
	match,
	markets,
	isLoading,
	hasMoreAsianLines,
	homeTeamName,
	awayTeamName,
}: MatchBettypesProps) => {
	const { sport, oddsType, actStatus } = useOddsTableMarsContext();
	const t = useTranslations('General');
	const betTypeTranslate = useTranslations('BetType');
	const [isArranging, setIsArranging] = useState<boolean>(true);
	const [betChoices, setBetChoices] = useState<Map<number, BetChoice[]>>(new Map());
	const [betTypeMarketGroups, setBetTypeMarketGroups] = useState<BetTypeMarket[][]>([]);
	const { breakpoint } = useBreakpoint();
	const sportSetting = getSportSetting(sport);
	const preferredMainBetType = usePreferredMainBetType();
	const sortedMainBetTypes = oddsPageService.getSortedMainBetTypes(sport, preferredMainBetType);
	const betTypesSet = new Set(sortedMainBetTypes);
	const allBetTypes: BetTypeGroup[] = matchEquals(sport)
		.on(43, () => sportSetting.oddsPageFilterBetTypes.map(criterion => [criterion.betTypes[0], criterion.resourceId]))
		.otherwise(() => Array.from(betTypesSet).map(betType => [betType, undefined]));
	const getGoal = useGetGoal();
	const getBetTypeSetting = useGetBetTypeSetting();
	const sortBetteams = ['1', '2', 'x'];
	const containerRef = useRef<HTMLDivElement | null>(null);
	const [scrollLeft, setScrollLeft] = useState(0);
	const handleScroll = () => {
		if (containerRef.current) {
			const leftDistance = containerRef.current.scrollLeft;
			setScrollLeft(leftDistance);
		}
	};
	const [paginationCount, setPaginationCount] = useState(1);

	useEffect(() => {
		betTypeMarketGroups.length > 0 && setPaginationCount(betTypeMarketGroups.length)
	}, [betTypeMarketGroups])

	useEffect(() => {
		const choiceGroup = new Map<number, BetChoice[]>();
		[...betTypesSet].forEach(betType => {
			const { betChoices } = getBetTypeSetting(betType, match.GameID);
			choiceGroup.set(
				betType,
				MaybeMatch([5, 15].includes(betType))
					.fold(
						() => betChoices,
						() => [...betChoices].sort((a, b) => sortBetteams.indexOf(a.betTeam) - sortBetteams.indexOf(b.betTeam))
					)
			)
		})
		setBetChoices(choiceGroup)
	}, [])

	useEffect(() => {
		MaybeMatch(!isLoading)
			.chain(() => itemsEmptyCheck(markets))
			.chain(() => MaybeMatch(betChoices.size > 0))
			.fold(
				() => [],
				() => IO.of(markets)
					.map((markets: Market[]) => sport === 43 ? getSortedESportMarkets(betTypesSet, match.IsLive, markets) : getSortedMarkets(betTypesSet, markets))
					.map((markets: Market[]) => {
						let betTypeMartkets: BetTypeMarket[] = allBetTypes.map(([betType, resourceId]) => {
							return [[betType, resourceId], markets.find(market => market.BetTypeId === betType && (resourceId == undefined || market.Resourceid === resourceId))];
						});
						switch (sport) {
							case 43:
								/**
								 * 9001 special ruls
								 * [1,2,3,4,5,6,7,8,9] => [[1,2,3], [4,5,6], [7,8,9]], and get first group which has market
								 */
								const esports9001Markets = toChunks(3)(betTypeMartkets.filter(([[betType]]) => betType === 9001)).find(group => group.some(([_, market]) => market)) ?? [];
								const esportsOtherMarkets = betTypeMartkets.filter(([[betType]]) => betType !== 9001);
								betTypeMartkets = match.IsLive ? [...esports9001Markets, ...esportsOtherMarkets] : [...esportsOtherMarkets, ...esports9001Markets];
								break;
							case 55:
								const oeMarketsIndex =  betTypeMartkets.findIndex(([[betType], market]) => market && betType === 2);
								const oeMarkets = betTypeMartkets.splice(oeMarketsIndex, 1);
								betTypeMartkets = oeMarketsIndex > -1 ? oeMarkets : betTypeMartkets;
								break;
						}
						const groups = betTypeMartkets.length > 3 ? [betTypeMartkets.slice(0, 3), betTypeMartkets.slice(3)] : [[...betTypeMartkets]];
						return breakpoint >= Breakpoint.md ? groups : groups.filter(group => group.some(([_, market]) => market));
					})
					.map(setBetTypeMarketGroups)
					.map(() => isArranging && setIsArranging(false))
					.run()
			)
	}, [isLoading, markets, breakpoint])
	
	return (
		<MatchOddsLoadingWrapper isLoading={isLoading || isArranging}>
			<div 
				className={css(matchStyles.bettypeWraper)}
				ref={containerRef}
				onScroll={handleScroll}
			>
				{
					betTypeMarketGroups.map((betTypeMartkets, index) => {
						const isEmptyGroup = !betTypeMartkets.some(([_, market]) => market);
						return (
							<div
								className={css(matchStyles.bettypes)}
								key={index}
								data-more-lines={hasMoreAsianLines}
							>
								{betTypeMartkets.map(([[betType, resourceId], market], index) => {
									const betTypeTitle = Box(betTypeService.getBetTypeSettingTitle(betType, match?.GameID ?? 0, market?.Resourceid ?? ''))
										.map(([label, translateType]: [string, TranslateType]) => matchEquals(translateType)
											.on('General', () => t.raw(label))
											.on('BetType', () => betTypeTranslate(label))
											.otherwise(() => '')
										)
										.map((translatedTitle: string) => betTypeService.getBetTypeReplaceTitle(
											translatedTitle,
											match?.GameID ?? 0,
											market ?? { Resourceid: resourceId, BetTypeId: betType } as Market
										))
										.extract();
									return (
										<Fragment key={`${betType}${resourceId}`}>
											<div className={css(matchStyles.bettype)}>
												<div className={clsx('bettype-title', matchStyles.bettypeTitle)}>
													<div className="multi-line-ellipsis">{betTypeTitle}</div>
												</div>
												{market && betChoices.get(market.BetTypeId)?.map(betChoice => {
													const displayClassName = betTypeService.getBetChoiceDisplaySettingBetTypeClassName(market.BetTypeId, betChoice.betTeam);
													return (
														<OddsButton
															key={`${market.MatchId}_${market.MarketId}_${market.BetTypeId}_${betChoice.betTeam}`}
															goal={getGoal(market, betChoice)}
															match={match}
															market={market}
															selection={market.Selections[betChoice.betTeam]}
															homeTeamName={homeTeamName}
															awayTeamName={awayTeamName}
															variant={displayClassName}
															betTeam={market.Selections[betChoice.betTeam].SelId}
															originalPrice={market.Selections[betChoice.betTeam].Price}
															sport={sport}
															type="OU"
															oddsType={oddsType}
															accountStatus={actStatus ?? 0}
														/>
													);
												})}
											</div>
											{!isEmptyGroup && hasMoreAsianLines && (index + 1) % 3 === 0 && (
												<MoreLinesButton 
													sport={sport}
													betType={betTypeMartkets.find(([[betType]]) => asianLineBetTypes.includes(betType))?.[0]?.[0] ?? 0} 
													isMoreAsianLineFullWidth={sport === 1 ? !betTypeMartkets.find(([[betType]]) => [5, 15, 20, 21].includes(betType))?.[1] : true}
													preferredMainBetType={preferredMainBetType}
												/>
											)}
										</Fragment>

									);
								})}
							</div>
						);
					})
				}
			</div>
			<OddsTablePagination paginationCount={paginationCount} scrollLeft={scrollLeft}  />
		</MatchOddsLoadingWrapper>
	)
}
